import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Tabs,
  Table,
  Tag,
  Spin,
  DatePicker,
  Select,
  Input,
  Button,
  Alert,
  Segmented,
  Switch,
} from 'antd';
import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';
import { formatCurrency } from '../utils/currency';
import { useFeedback } from '../utils/feedback';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import { ORDER_STATUS_COLORS } from '../utils/orderStatus';
import FixedCosts from './FixedCosts';
import CostInvoices from '../modules/companyCosts/CostInvoices';
import IncomeStatementWorkspace from '../modules/reports/incomeStatement/IncomeStatementWorkspace';
import {
  resolveCompanyBalanceRange,
  type CompanyBalancePeriod,
} from '../modules/reports/companyBalance/companyBalancePeriod';
import ProfitabilityAnalysisWorkspace from '../modules/reports/profitabilityAnalysis/ProfitabilityAnalysisWorkspace';
import { fetchCustomerOptions } from '../modules/reference-data/api/referenceDataApi';
import type { AnalysisCustomerRef } from '../modules/reports/profitabilityAnalysis/analysis';
import {
  displayMarginPercent,
  getMarginBucket,
  MARGIN_BUCKET_THRESHOLDS,
} from '../modules/reports/profitabilityAnalysis/analysis';
import type {
  AnalysisComparison,
  AnalysisDimension,
  AnalysisMetric,
  AnalysisProduct,
  MarginBucket,
} from '../modules/reports/profitabilityAnalysis/analysis';
import './Profitability.css';

const { RangePicker } = DatePicker;

// ─── DTOs ────────────────────────────────────────────────────────────────────

interface OrderRow {
  id: string;
  orderNumber: string;
  manualOrderNumber?: string;
  useManualNumbering?: boolean;
  orderDate?: string;
  customer?: { id?: string; name: string };
  status: string;
  isSettled?: boolean;
  revenue: number;
  // `null` means an unknown cost/margin (e.g. a pending WAREHOUSE_FIFO batch),
  // never a real zero — see analysis.ts `AnalysisOrder`.
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
  products?: AnalysisProduct[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

// Range picker value tuple (Dayjs|null for each side, or null for the whole tuple).
type RangeValue = [Dayjs | null, Dayjs | null] | null;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function num(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

// Cost/margin/marginPercent stay `null` (an unknown pending-cost order),
// never coerced to a fake 0 — see analysis.ts `AnalysisOrder`.
function nullableNum(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function displayOrderNumber(o: Pick<OrderRow, 'orderNumber' | 'manualOrderNumber' | 'useManualNumbering'>): string {
  return o.useManualNumbering && o.manualOrderNumber ? o.manualOrderNumber : o.orderNumber;
}

// ─── Component ───────────────────────────────────────────────────────────────

const TAB_KEYS = ['income-statement', 'analysis', 'fixed-costs', 'cost-invoices'] as const;
type TabKey = typeof TAB_KEYS[number];

function isTabKey(value: string | null): value is TabKey {
  return !!value && (TAB_KEYS as readonly string[]).includes(value);
}

const Profitability: React.FC = () => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'common']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Tab state lives in the URL so links/reloads land on the right view ──
  const requestedTab = searchParams.get('tab');
  const activeTab: TabKey = requestedTab === 'orders' || requestedTab === 'budget'
    ? 'analysis'
    : (requestedTab === 'reports' || requestedTab === 'overview' || requestedTab === 'stock-purchases')
      ? 'income-statement'
    : isTabKey(requestedTab) ? requestedTab : 'income-statement';

  useEffect(() => {
    if (requestedTab === 'batches') {
      navigate('/app/inventory/batch-profitability', { replace: true });
      return;
    }
    if (requestedTab === 'orders' || requestedTab === 'budget' || (requestedTab === 'reports' || requestedTab === 'overview' || requestedTab === 'stock-purchases')) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', (requestedTab === 'reports' || requestedTab === 'overview' || requestedTab === 'stock-purchases') ? 'income-statement' : 'analysis');
      navigate(`/app/profitability?${next.toString()}`, { replace: true });
    }
  }, [navigate, requestedTab, searchParams]);

  function handleTabChange(key: string) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next);
  }

  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  // Every customer, so the ranking can list companies without orders as zeros.
  const [customers, setCustomers] = useState<AnalysisCustomerRef[]>([]);

  // ── Filter state (client-side; orders are already fully loaded) ─────────
  const [dateRange, setDateRange] = useState<RangeValue>(null);
  // Quick period switch (same presets as the Bilans tab). `null` means the
  // operator picked a custom range in the RangePicker or cleared the filter.
  const [analysisPeriod, setAnalysisPeriod] = useState<CompanyBalancePeriod | null>(null);
  const [analysisAnchor, setAnalysisAnchor] = useState<Dayjs>(dayjs());
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [hideZeroCost, setHideZeroCost] = useState<boolean>(false);
  // "Tylko rozliczone": keep only orders with at least one settlement entry
  // (`isSettled` from the order list), so forecasts of in-progress orders can
  // be excluded without hunting through statuses.
  const [onlySettled, setOnlySettled] = useState<boolean>(false);
  const [bucketFilter, setBucketFilter] = useState<MarginBucket[]>([]);
  const [analysisMetric, setAnalysisMetric] = useState<AnalysisMetric>('margin');
  const [analysisDimension, setAnalysisDimension] = useState<AnalysisDimension>('customer');
  const [analysisComparison, setAnalysisComparison] = useState<AnalysisComparison>('previousMonth');

  useEffect(() => {
    loadOrders();
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Deep-link date filter from the "Przegląd" tab (e.g. a daily-drilldown
  // row's "view in Analiza" link): ?tab=analysis&dateFrom=...&dateTo=...
  // seeds the range picker once per distinct incoming pair, then the filter
  // bar owns the value like any other manual selection.
  const dateFromParam = searchParams.get('dateFrom');
  const dateToParam = searchParams.get('dateTo');
  useEffect(() => {
    if (!dateFromParam && !dateToParam) return;
    // Hand-crafted URLs can carry garbage — an invalid Dayjs seeded into the
    // RangePicker renders as "Invalid Date", so validate before seeding.
    const from = dateFromParam ? dayjs(dateFromParam) : null;
    const to = dateToParam ? dayjs(dateToParam) : null;
    if ((from && !from.isValid()) || (to && !to.isValid())) return;
    setDateRange([from, to]);
    setAnalysisPeriod(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFromParam, dateToParam]);

  async function loadCustomers() {
    try {
      const list = await fetchCustomerOptions('profitability.customers');
      setCustomers(list.filter((c) => c.id && c.name).map((c) => ({ id: c.id, name: c.name })));
    } catch {
      // Ranking still works from orders alone; missing customers simply are not appended.
    }
  }

  async function loadOrders() {
    setOrdersLoading(true);
    try {
      const res = await api.get('/orders', {
        params: { isQuote: 'false', sortBy: 'orderDate', sortOrder: 'desc' },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      const normalised: OrderRow[] = list.map((o: OrderRow) => ({
        ...o,
        revenue: num(o.revenue),
        totalCost: nullableNum(o.totalCost),
        margin: nullableNum(o.margin),
        marginPercent: nullableNum(o.marginPercent),
        products: (o.products || []).map((product) => ({
          ...product,
          quantity: num(product.quantity),
          saleQuantity: num(product.saleQuantity ?? product.quantity),
          unitPrice: num(product.unitPrice),
          totalPrice: num(product.totalPrice),
        })),
      }));
      setOrders(normalised);
    } catch {
      feedback.error(t('reports:profitability.loadOrdersFailed'));
    } finally {
      setOrdersLoading(false);
    }
  }

  // ── Distinct statuses present in current dataset ────────────────────────
  const availableStatuses = useMemo(() => {
    const set = new Set<string>();
    orders.forEach((o) => o.status && set.add(o.status));
    return Array.from(set);
  }, [orders]);

  // ── Filtered orders (client-side) ───────────────────────────────────────
  // Keep the bucket filter separate so its counts remain visible after a
  // bucket is selected. This also lets operators combine it with the other
  // filters without making the remaining bucket chips disappear.
  const filteredWithoutBucket = useMemo(() => {
    return orders.filter((o) => {
      if (hideZeroCost && (o.totalCost ?? 0) <= 0) return false;
      if (onlySettled && !o.isSettled) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(o.status)) return false;
      if (customerSearch) {
        const q = customerSearch.trim().toLowerCase();
        if (!o.customer?.name?.toLowerCase().includes(q)) return false;
      }
      if (dateRange && (dateRange[0] || dateRange[1]) && o.orderDate) {
        const d = dayjs(o.orderDate);
        if (dateRange[0] && d.isBefore(dateRange[0].startOf('day'))) return false;
        if (dateRange[1] && d.isAfter(dateRange[1].endOf('day'))) return false;
      }
      return true;
    });
  }, [orders, hideZeroCost, onlySettled, statusFilter, customerSearch, dateRange]);

  // Customers appended as zero rows follow the same customer search as the
  // orders, so a name filter narrows the ranking instead of listing every company.
  const rankedCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => c.name.toLowerCase().includes(q));
  }, [customers, customerSearch]);

  const filtered = useMemo(() => {
    if (bucketFilter.length === 0) return filteredWithoutBucket;
    return filteredWithoutBucket.filter((o) => (
      bucketFilter.includes(getMarginBucket(o))
    ));
  }, [filteredWithoutBucket, bucketFilter]);

  const attentionOrders = useMemo(() => filtered
    .filter((o) => (o.totalCost ?? 0) <= 0 || (o.margin ?? 0) < 0 || (o.marginPercent ?? 0) < MARGIN_BUCKET_THRESHOLDS.low)
    .sort((a, b) => {
      const aMargin = a.margin ?? 0;
      const bMargin = b.margin ?? 0;
      const aCost = a.totalCost ?? 0;
      const bCost = b.totalCost ?? 0;
      if (aMargin < 0 && bMargin >= 0) return -1;
      if (bMargin < 0 && aMargin >= 0) return 1;
      if (aCost <= 0 && bCost > 0) return -1;
      if (bCost <= 0 && aCost > 0) return 1;
      return (a.marginPercent ?? 0) - (b.marginPercent ?? 0);
    })
    .slice(0, 4), [filtered]);

  // Distribution after the regular filters, before the bucket filter — drives
  // the clickable chips and keeps all available choices visible.
  const bucketCounts = useMemo(() => {
    const counts: Record<MarginBucket, number> = {
      negative: 0, low: 0, medium: 0, good: 0, excellent: 0, none: 0,
    };
    filteredWithoutBucket.forEach((o) => {
      counts[getMarginBucket(o)] += 1;
    });
    return counts;
  }, [filteredWithoutBucket]);

  function toggleBucket(bucket: MarginBucket) {
    setBucketFilter((current) => (
      current.includes(bucket)
        ? current.filter((selected) => selected !== bucket)
        : [...current, bucket]
    ));
  }

  function applyAnalysisPeriod(period: CompanyBalancePeriod, anchor: Dayjs) {
    const range = resolveCompanyBalanceRange(anchor, period);
    setAnalysisPeriod(period);
    setAnalysisAnchor(anchor);
    setDateRange([range.start, range.end]);
  }

  function clearAnalysisPeriod() {
    setAnalysisPeriod(null);
    setDateRange(null);
  }

  function resetFilters() {
    setDateRange(null);
    setAnalysisPeriod(null);
    setStatusFilter([]);
    setCustomerSearch('');
    setHideZeroCost(false);
    setOnlySettled(false);
    setBucketFilter([]);
  }

  // ── Orders table columns ────────────────────────────────────────────────
  const orderColumns = useMemo(() => [
    {
      title: t('reports:profitability.colOrder'),
      key: 'orderNumber',
      width: 130,
      sorter: (a: OrderRow, b: OrderRow) =>
        displayOrderNumber(a).localeCompare(displayOrderNumber(b)),
      render: (_: unknown, record: OrderRow) => (
        <Link to={`/app/orders/${record.id}`}>{displayOrderNumber(record)}</Link>
      ),
    },
    {
      title: t('reports:profitability.colCustomer'),
      key: 'customer',
      sorter: (a: OrderRow, b: OrderRow) =>
        (a.customer?.name || '').localeCompare(b.customer?.name || ''),
      render: (_: unknown, record: OrderRow) => record.customer?.name ?? '—',
    },
    {
      title: t('reports:profitability.colOrderDate'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      defaultSortOrder: 'descend' as const,
      sorter: (a: OrderRow, b: OrderRow) =>
        (a.orderDate ? dayjs(a.orderDate).valueOf() : 0) -
        (b.orderDate ? dayjs(b.orderDate).valueOf() : 0),
      render: (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: t('reports:profitability.colStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      sorter: (a: OrderRow, b: OrderRow) => a.status.localeCompare(b.status),
      render: (status: string) => (
        <Tag color={ORDER_STATUS_COLORS[status] ?? 'default'}>
          {t(`orders:statusOptions.${status}`, { defaultValue: t('common:unknownStatus') })}
        </Tag>
      ),
    },
    {
      title: t('reports:profitability.colRevenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      width: 140,
      sorter: (a: OrderRow, b: OrderRow) => a.revenue - b.revenue,
      render: (v: number) => formatCurrency(v, 'PLN'),
    },
    {
      title: t('reports:profitability.colCost'),
      dataIndex: 'totalCost',
      key: 'totalCost',
      align: 'right' as const,
      width: 140,
      sorter: (a: OrderRow, b: OrderRow) => (a.totalCost ?? 0) - (b.totalCost ?? 0),
      render: (v: number | null) => (v != null && v > 0 ? formatCurrency(v, 'PLN') : <span className="profitability-margin-cell--muted">—</span>),
    },
    {
      title: t('reports:profitability.colMargin'),
      dataIndex: 'margin',
      key: 'margin',
      align: 'right' as const,
      width: 140,
      sorter: (a: OrderRow, b: OrderRow) => (a.margin ?? 0) - (b.margin ?? 0),
      render: (v: number | null, record: OrderRow) => {
        if ((record.totalCost ?? 0) <= 0 || v == null) return <span className="profitability-margin-cell--muted">—</span>;
        const cls = v < 0
          ? 'profitability-margin-cell profitability-margin-cell--negative'
          : 'profitability-margin-cell profitability-margin-cell--positive';
        return <span className={cls}>{formatCurrency(v, 'PLN')}</span>;
      },
    },
    {
      title: t('reports:profitability.colMarginPercent'),
      dataIndex: 'marginPercent',
      key: 'marginPercent',
      align: 'right' as const,
      width: 110,
      sorter: (a: OrderRow, b: OrderRow) => (a.marginPercent ?? 0) - (b.marginPercent ?? 0),
      render: (_v: number | null, record: OrderRow) => {
        const percent = displayMarginPercent(record);
        if ((record.totalCost ?? 0) <= 0 || percent == null) return <span className="profitability-margin-cell--muted">—</span>;
        const cls = percent < 0
          ? 'profitability-margin-cell profitability-margin-cell--negative'
          : 'profitability-margin-cell profitability-margin-cell--positive';
        return <span className={cls}>{formatLocalizedPercent(percent, i18n.language)}</span>;
      },
    },
  ], [i18n.language, t]);

  function rowClassName(record: OrderRow): string {
    const bucket = getMarginBucket(record);
    return `profitability-row profitability-row--${bucket}`;
  }

  // Trend visualization now lives on the "Przegląd" tab (ProfitabilityOverview),
  // which reads server-aggregated monthly/daily totals instead of re-deriving
  // them client-side from this filtered order slice. This tab keeps the
  // attention list — it reacts to the interactive filters above it.
  const attentionCard = (
    <Card className="profitability-attention-card" title={<span>{t('reports:profitability.attention.title')} <Tag color={attentionOrders.length ? 'error' : 'success'}>{attentionOrders.length}</Tag></span>}>
      {attentionOrders.length === 0 ? <Alert type="success" showIcon message={t('reports:profitability.attention.clear')} /> : attentionOrders.map((o) => {
        const missing = (o.totalCost ?? 0) <= 0 || o.margin == null;
        const loss = !missing && (o.margin as number) < 0;
        const percent = displayMarginPercent(o);
        return (
          <Link className={`profitability-attention-item ${loss ? 'is-loss' : ''}`} to={`/app/orders/${o.id}`} key={o.id}>
            {loss ? <ExclamationCircleOutlined /> : <WarningOutlined />}
            <span>
              <strong>{loss ? t('reports:profitability.attention.loss') : missing ? t('reports:profitability.attention.missingCosts') : t('reports:profitability.attention.lowMargin')}</strong>
              <small>{displayOrderNumber(o)} · {o.customer?.name || '—'}</small>
            </span>
            <b>{missing ? '—' : loss ? formatCurrency(o.margin as number, 'PLN') : (percent == null ? '—' : formatLocalizedPercent(percent, i18n.language))}</b>
          </Link>
        );
      })}
    </Card>
  );

  // ── Bucket legend (uses the same row-tint dots) ─────────────────────────
  const bucketOrder: MarginBucket[] = ['negative', 'low', 'medium', 'good', 'excellent', 'none'];
  const bucketLegend = (
    <div className="profitability-bucket-legend">
      {bucketOrder
        .filter((b) => bucketCounts[b] > 0)
        .map((b) => (
          <button
            key={b}
            type="button"
            className={`profitability-bucket-chip ${bucketFilter.includes(b) ? 'is-active' : ''}`}
            aria-pressed={bucketFilter.includes(b)}
            onClick={() => toggleBucket(b)}
          >
            <span className={`profitability-bucket-dot profitability-bucket-dot--${b}`} />
            {t(`reports:profitability.buckets.${b}`, {
              low: MARGIN_BUCKET_THRESHOLDS.low,
              medium: MARGIN_BUCKET_THRESHOLDS.medium,
              good: MARGIN_BUCKET_THRESHOLDS.good,
            })}
            <span className="profitability-bucket-chip__count">{bucketCounts[b]}</span>
          </button>
        ))}
    </div>
  );

  // ── Filter bar ──────────────────────────────────────────────────────────
  const periodBar = (
    <div className="profitability-period-bar">
      <div>
        <span>{t('reports:profitability.filters.period')}</span>
        <DatePicker
          value={analysisAnchor}
          onChange={(value) => value && applyAnalysisPeriod(analysisPeriod ?? 'MONTH', value)}
          picker={analysisPeriod === 'YEAR' ? 'year' : analysisPeriod === 'QUARTER' ? 'quarter' : 'month'}
          allowClear={false}
        />
      </div>
      <Segmented<CompanyBalancePeriod | 'ALL'>
        value={analysisPeriod ?? 'ALL'}
        onChange={(period) => (period === 'ALL' ? clearAnalysisPeriod() : applyAnalysisPeriod(period, analysisAnchor))}
        options={[
          { value: 'ALL', label: t('reports:profitability.filters.periodAll') },
          { value: 'MONTH', label: t('reports:companyBalance.periods.month') },
          { value: 'QUARTER', label: t('reports:companyBalance.periods.quarter') },
          { value: 'HALF_YEAR', label: t('reports:companyBalance.periods.halfYear') },
          { value: 'YEAR', label: t('reports:companyBalance.periods.year') },
        ]}
      />
    </div>
  );

  const filterBar = (
    <div className="profitability-filters-bar" aria-label={t('reports:profitability.filters.ariaLabel')}>
      <RangePicker
        value={dateRange}
        onChange={(range) => {
          setDateRange(range as RangeValue);
          setAnalysisPeriod(null);
        }}
        allowEmpty={[true, true]}
        aria-label={t('reports:profitability.filters.dateRange')}
      />
      <Select
        mode="multiple"
        maxTagCount="responsive"
        value={statusFilter}
        onChange={setStatusFilter}
        placeholder={t('reports:profitability.filters.statusPlaceholder')}
        aria-label={t('reports:profitability.filters.status')}
        options={availableStatuses.map((s) => ({
          value: s,
          label: t(`orders:statusOptions.${s}`, { defaultValue: t('common:unknownStatus') }),
        }))}
        allowClear
      />
      <Input
        value={customerSearch}
        onChange={(e) => setCustomerSearch(e.target.value)}
        placeholder={t('reports:profitability.filters.customerPlaceholder')}
        aria-label={t('reports:profitability.filters.customer')}
        allowClear
      />
      <Select
        value={hideZeroCost ? 'withCosts' : 'all'}
        onChange={(value) => setHideZeroCost(value === 'withCosts')}
        aria-label={t('reports:profitability.filters.costScope')}
        options={[
          { value: 'all', label: t('reports:profitability.filters.costScopeAll') },
          { value: 'withCosts', label: t('reports:profitability.filters.costScopeWithCosts') },
        ]}
      />
      <label className="profitability-filters__toggle">
        <Switch
          size="small"
          checked={onlySettled}
          onChange={setOnlySettled}
          aria-label={t('reports:profitability.filters.onlySettled')}
        />
        <span>{t('reports:profitability.filters.onlySettled')}</span>
      </label>
      <Button onClick={resetFilters} type="text">
        {t('reports:profitability.filters.reset')}
      </Button>
      <span className="profitability-filters__counter">
        {t('reports:profitability.filters.showingCount', {
          shown: filtered.length,
          total: orders.length,
        })}
      </span>
    </div>
  );

  // ── Tabs ────────────────────────────────────────────────────────────────
  const analysisTab = (
    <div className="profitability-page">
      <Alert
        type="info"
        showIcon
        message={t('reports:profitability.analysis.operationalNotice')}
        description={t('reports:profitability.analysis.operationalNoticeDescription')}
        style={{ marginBottom: 16 }}
      />
      {periodBar}
      {filterBar}
      <ProfitabilityAnalysisWorkspace
        orders={filtered}
        customers={rankedCustomers}
        totalOrders={orders.length}
        metric={analysisMetric}
        dimension={analysisDimension}
        comparison={analysisComparison}
        onMetricChange={setAnalysisMetric}
        onDimensionChange={setAnalysisDimension}
        onComparisonChange={setAnalysisComparison}
      />
      {attentionCard}
      {bucketLegend}
      {ordersLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table<OrderRow>
          dataSource={filtered}
          columns={orderColumns}
          rowKey="id"
          rowClassName={rowClassName}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            defaultPageSize: 25,
          }}
        />
      )}
    </div>
  );

  const tabItems = [
    {
      key: 'income-statement',
      label: t('reports:profitability.tabIncomeStatement'),
      children: <IncomeStatementWorkspace />,
    },
    {
      key: 'analysis',
      label: t('reports:profitability.tabAnalysis'),
      children: analysisTab,
    },
    {
      key: 'fixed-costs',
      label: t('reports:profitability.tabFixedCosts'),
      children: <FixedCosts />,
    },
    { key: 'cost-invoices', label: t('reports:profitability.tabCostInvoices'), children: <CostInvoices /> },
  ];

  return (
    <div className="profitability-shell">
      <header className="profitability-shell__header">
        <div>
          <span>{t('reports:profitability.workspaceKicker')}</span>
          <h1>{t('reports:profitability.workspaceTitle')}</h1>
          <p>{t('reports:profitability.workspaceDescription')}</p>
        </div>
      </header>
      <Tabs destroyInactiveTabPane items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
    </div>
  );
};

export default Profitability;
